import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  FormErrorMessage,
  Checkbox,
  Text,
} from "@chakra-ui/react";
import { constant } from "common/constant";
import { useEffect, useState } from "react";

const DynamicFormModal = ({
  isOpen,
  onClose,
  title,
  fields = [],
  onSubmit,
  initialValues = {},
  moduleFields = [],
}) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [dynamicFields, setDynamicFields] = useState(fields);

  const handleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));

    if (key === "field") {
      const selectedField = moduleFields.find((field) => field.name === value);
      if (selectedField) {
        const updatedFields = fields.map((field) => {
          if (field.key === "value") {
            if (selectedField.type === "select") {
              return {
                ...field,
                type: "select",
                options: selectedField.options.map((option) => ({
                  value: option.value || option,
                  label: option.name || option,
                })),
              };
            } else if (selectedField.type === "number") {
              return {
                ...field,
                type: "number",
              };
            } else {
              return {
                ...field,
                type: "text",
              };
            }
          }
          return field;
        });
        setDynamicFields(updatedFields);
      }
    }
  };

  const validateFields = () => {
    const newErrors = {};
    dynamicFields.forEach((field) => {
      const { key, label, validation = { required: true }, type } = field;
      const value = formData[key];

      const trimmedValue = typeof value === "string" ? value.trim() : value;

      if (validation.required && !trimmedValue && trimmedValue !== 0) {
        newErrors[key] = `${label} is required`;
      }
      if (
        (type === "textarea" || type === "text") &&
        validation.maxLength &&
        trimmedValue &&
        trimmedValue.length > validation.maxLength
      ) {
        newErrors[
          key
        ] = `${label} must be at most ${validation.maxLength} characters`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateFields()) {
      onSubmit(formData);
      onClose();
      setFormData({});
      setErrors({});
    }
  };

  useEffect(() => {
    if (isOpen) {
      setFormData({ ...initialValues });
      setErrors({});
      setDynamicFields(fields);
    }
  }, [isOpen, fields]);

  const renderField = (field) => {
    const { key, label, type, placeholder, options = [] } = field;
    const value = formData[key] || (type === "checkbox" ? false : "");
    const error = errors[key];

    let inputElement;
    switch (type) {
      case "text":
      case "number":
      case "datetime-local":
        inputElement = (
          <Input
            type={type}
            value={value}
            placeholder={placeholder}
            onChange={(e) => handleChange(key, e.target.value)}
          />
        );
        break;
      case "textarea":
        inputElement = (
          <>
            <Textarea
              value={value}
              placeholder={placeholder}
              onChange={(e) => {
                const limitedText = e.target.value.slice(0, 150);
                handleChange(key, limitedText);
              }}
            />
            <Text
              mt={1}
              fontSize="xs"
              textAlign="right"
              color={(value?.length || 0) === 150 ? "red.500" : "gray.500"}
            >
              {0 + (value?.length || 0)}/150
            </Text>
          </>
        );
        break;

      case "select":
        inputElement = (
          <Select
            value={value}
            placeholder={placeholder || "Select option"}
            onChange={(e) => handleChange(key, e.target.value)}
          >
            {options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        );
        break;

      case "file":
        inputElement = (
          <Input
            type="file"
            accept=".html,.txt"
            onChange={(e) => handleChange(key, e.target.files[0])}
          />
        );
        break;

      case "checkbox":
        inputElement = (
          <Checkbox
            isChecked={value}
            onChange={(e) => handleChange(key, e.target.checked)}
          >
            {label}
          </Checkbox>
        );
        break;

      default:
        inputElement = null;
    }

    return (
      <FormControl
        key={key}
        mb={4}
        isInvalid={!!error}
        className="workflowFormControl"
      >
        {type !== "checkbox" && (
          <FormLabel className="workflowLabel">{label}</FormLabel>
        )}
        {inputElement}
        {error && <FormErrorMessage>{error}</FormErrorMessage>}
      </FormControl>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent maxW={"524px"} width={"100%"}>
        <ModalHeader>{title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>{dynamicFields?.map(renderField)}</ModalBody>
        <ModalFooter>
          <Button
            variant="outline"
            colorScheme="red"
            size="sm"
            sx={{ marginRight: 4, textTransform: "capitalize" }}
            onClick={onClose}
            className="theme-btn2 cancelBtn"
          >
            {constant.cancel}
          </Button>
          <Button
            variant={"brand"}
            className="theme-btn1"
            onClick={handleSubmit}
          >
            {constant.next}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default DynamicFormModal;
